<template>
  <DsPageContent>
    <!-- Page: Header -->
    <DsSection :with-space-top="false">
      <div class="ds-grid">
        <div class="ds-grid__col">
          <DsHeaderWithDivider
            v-if="pageContent.content.image"
            :image="pageContent.content.image"
            :screen="screen"
            theme="light"
            class="header"
          />
        </div>
      </div>
      <div class="ds-container ds-margin-top-48 ds-lg-margin-top-0">
        <div class="ds-grid">
          <div class="ds-grid__col ds-grid__col--12 ds-lg-grid__col--8 ds-lg-grid__col--offset-2 ds-lg-padding-x-0">
            <DsBreadcrumbs
              v-if="data.breadcrumbs"
              :crumbs="data.breadcrumbs"
              class="ds-margin-bottom-24 ds-lg-margin-bottom-48"
            />
            <h1
              v-if="pageContent.content.title"
              class="ds-heading-large ds-margin-bottom-12"
            >
              {{ pageContent.content.title }}
            </h1>
            <RichText
              v-if="pageContent.content.longDescription"
              :data="pageContent.content.longDescription"
              class="ds-text-medium ds-margin-bottom-24"
            />
          </div>
        </div>
      </div>
    </DsSection>
    <section class="ds-padding-top-48 ds-lg-padding-top-0 ds-padding-bottom-48 ds-padding-bottom-96">
      <div class="ds-container">
        <div class="ds-grid">
          <div class="ds-grid__col ds-grid__col--12 ds-lg-grid__col--11 ds-lg-grid__col--offset-1">
            <!-- Page: Cards Stats -->
            <div
              v-if="cardStats"
              class="ds-grid ds-padding-y-48 ds-lg-padding-y-96 ds-align-items-center"
            >
              <div
                v-for="(item, index) in cardStats"
                :key="`card-stat-${index}`"
                class="ds-grid__col ds-grid__col--12 ds-md-grid__col--4 ds-padding-y-12 ds-lg-padding-y-0"
              >
                <DsCardStats
                  v-if="item"
                  :icon="item.icon"
                  :eyebrow="item.label"
                  :title="item.data"
                />
              </div>
            </div>
            <!-- Page: Stats Bar -->
            <template v-if="statsBars.length">
              <DsStatsBar
                v-for="(item, index) in statsBars"
                :key="`stats-bar-${index}`"
                :items="item"
                :class="{ 'ds-margin-top-48': index > 0 }"
              />
            </template>
          </div>
        </div>
      </div>
    </section>

    <!-- Page: Related Payment Methods Gallery -->
    <DsSection
      v-if="pageContent.content.relatedPaymentMethods.length"
      :full-screen="true"
      theme="grey"
    >
      <div
        v-if="!mappedRelatedPaymentMethods"
        class="ds-display-flex ds-width-full ds-align-items-center ds-justify-content-center ds-padding-top-24"
      >
        <Loading />
      </div>

      <DsImageGallery
        v-else
        :screen="screen"
        :items="pageContent.content.relatedPaymentMethods.length"
        :width="{ small: 272, large: 264 }"
        :title="filterLabels.relatedPaymentMethodsTitle"
      >
        <template #default="{ index }">
          <PaymentMethodsCard
            :link="mappedRelatedPaymentMethods[index - 1]?.url"
            :image="
              mappedRelatedPaymentMethods[index - 1]?.image && mappedRelatedPaymentMethods[index - 1]?.image.original
            "
            :title="mappedRelatedPaymentMethods[index - 1]?.name"
            :subtitle="mappedRelatedPaymentMethods[index - 1]?.paymentMethodTypesNames"
            :description="mappedRelatedPaymentMethods[index - 1]?.shortDescription"
            height="378px"
            theme-light
          />
        </template>
      </DsImageGallery>
    </DsSection>

    <!-- Page: Call to Action -->
    <DsCallToAction
      v-if="pageContent.content.callToAction"
      :title="pageContent.content.callToAction.title"
      :text="pageContent.content.callToAction.text"
      :primary-link="pageContent.content.callToAction.primaryLink"
      :secondary-link="pageContent.content.callToAction.secondaryLink"
      data-id="callToAction"
    />
  </DsPageContent>
</template>
<script setup>
import {
  DsBreadcrumbs,
  DsCallToAction,
  DsCardStats,
  DsHeaderWithDivider,
  DsImageGallery,
  DsPageContent,
  DsSection,
  DsStatsBar
} from 'awds'
import { kebabCase } from 'lodash-es'
import { mapperRelatedPaymentMethodsDetail } from '../../../data-layer/page-payment-methods-detail/mapper-related-payment-methods-detail'
import {
  queryPagePaymentMethodsGuidesRegionCountry,
  queryPagePaymentMethodsGuidesShort
} from '../../../data-layer/queries'
import { generateRegionCountryBreadcrumbs } from '~/data-layer/page-payment-methods-guides-region-country/mapper-page-breadcrumbs'

const store = useRoutesStore()
const { queryContentful } = useQueries()
const { dataPageMapper } = useDataMapper()
const { getSlug, getParentPath } = usePageUtils()
const { getLocaleIso } = useLocales()
const route = useRoute()
const { locale: localeRef } = useI18n()
const slug = getSlug(route.path)
const locale = getLocaleIso(localeRef.value)
const { screen } = useScreen()
const siteUrl = useRuntimeConfig().public.siteUrl

const { labels, mappers, onBeforeAdyenData, onAfterAdyenData } = usePaymentMethodsPageData()

await onBeforeAdyenData({ withRoutes: true, globalDataContentType: 'PagePaymentMethodsGuidesRegionCountry' })

const { data } = await useAsyncData(
  'payment-methods-guides-region-country-' + route.params.region + '-' + route.params.country + '-' + localeRef.value,
  async () => {
    const paymentMethodsGuidesQuery = queryContentful(
      {
        query: queryPagePaymentMethodsGuidesRegionCountry,
        variables: {
          slug,
          parentSlug: route.params.region,
          locale,
          preview: useRuntimeConfig().public.preview
        }
      },
      { log: false }
    )

    const paymentMethodsGuidesBasePageQuery = queryContentful(
      {
        query: queryPagePaymentMethodsGuidesShort,
        variables: { locale, preview: useRuntimeConfig().public.preview }
      },
      { log: false }
    )

    const [paymentMethodsGuidesData, paymentMethodsGuidesBasePageData] = await Promise.all([
      paymentMethodsGuidesQuery,
      paymentMethodsGuidesBasePageQuery
    ])

    if (!paymentMethodsGuidesData || !paymentMethodsGuidesBasePageData) {
      return null
    }

    const entry = paymentMethodsGuidesData.page.items[0]
    const basePageEntry = paymentMethodsGuidesBasePageData.page.items[0]

    if (!entry || !basePageEntry) {
      return null
    }

    const pageData = dataPageMapper(
      {
        ...entry,
        countryPages: paymentMethodsGuidesData.countryPages
      },
      mappers
    )

    try {
      await handleLazyUrlsMapping(pageData)
    } catch (e) {
      console.error("[ERROR]: Couldn't handle lazy urls in Payment Methods Guides Region Country", e)
    }

    /**
     * @typedef {Object} AsyncDataReturnType
     * @property {Object} pageData
     * @property {string} paymentMethodGuidesPageName
     * @property {import('#layers/core/utils/structuredData').BreadcrumbType[] | null} [breadcrumbs]
     * @property {import('#layers/core/types/seo/breadcrumb').BreadcrumbStructuredData | undefined} [structuredData]
     */

     /** @type {AsyncDataReturnType} data */
    const data = {
      pageData,
      paymentMethodGuidesPageName: basePageEntry.title
    }

    const breadcrumbs = generateRegionCountryBreadcrumbs({
      basePageEntry,
      getParentPath,
      localeRef,
      pageData,
      route,
      store
    });

    if (breadcrumbs) {
      data.breadcrumbs = breadcrumbs;
      data.structuredData = generateBreadcrumbStructuredData(breadcrumbs, siteUrl);
    }

    return data
  }
)
await onAfterAdyenData(data.value)

const pageContent = computed(() => {
  return data.value?.pageData
})

const filterLabels = computed(() => {
  return labels.value
})

const cardStats = computed(() => {
  return {
    population: pageContent.value.content.population
      ? {
          label: filterLabels.value.population,
          data: pageContent.value.content.population,
          icon: { category: 'ui-and-people', name: 'users-4' }
        }
      : null,
    currency: pageContent.value.content.currency.name
      ? {
          label: filterLabels.value.currency,
          data: pageContent.value.content.currency.name,
          icon: { category: 'financial', name: kebabCase(pageContent.value.content.currency.name) }
        }
      : null,
    cardsPerCapita: pageContent.value.content.cardsPerCapita
      ? {
          label: filterLabels.value.cardsPerCapita,
          data: pageContent.value.content.cardsPerCapita,
          icon: { category: 'financial', name: 'credit-card-2' }
        }
      : null
  }
})

const paymentsStatsBar = computed(() => {
  return [
    {
      text: filterLabels.value?.countryStatisticsOnlineBanking,
      percentage: pageContent.value?.content?.paymentsStatistics?.onlineBanking
    },
    {
      text: filterLabels.value?.countryStatisticsLocalCard,
      percentage: pageContent.value?.content?.paymentsStatistics?.localCard
    },
    {
      text: filterLabels.value?.countryStatisticsGlobalCard,
      percentage: pageContent.value?.content?.paymentsStatistics?.globalCard
    },
    {
      text: filterLabels.value?.countryStatisticsCash,
      percentage: pageContent.value?.content?.paymentsStatistics?.cash
    },
    {
      text: filterLabels.value?.countryStatisticsBuyNowPayLater,
      percentage: pageContent.value?.content?.paymentsStatistics?.buyNowPayLater
    },
    {
      text: filterLabels.value?.countryStatisticsWallet,
      percentage: pageContent.value?.content?.paymentsStatistics?.wallet
    },
    {
      text: filterLabels.value?.countryStatisticsPrepaidCard,
      percentage: pageContent.value?.content?.paymentsStatistics?.prepaidCard
    },
    {
      text: filterLabels.value?.countryStatisticsOther,
      percentage: pageContent.value?.content?.paymentsStatistics?.others
    }
  ].filter((item) => !!item.percentage)
})

const devicesStatsBar = computed(() => {
  return [
    {
      text: filterLabels.value.countryStatisticsDesktop,
      percentage: pageContent.value?.content?.devicesStatistics?.desktop
    },
    {
      text: filterLabels.value.countryStatisticsMobile,
      percentage: pageContent.value?.content?.devicesStatistics?.mobile
    }
  ].filter((item) => !!item.percentage)
})

const statsBars = computed(() => {
  const paymentBars = []
  if (paymentsStatsBar.value.length > 0) {
    paymentBars.push(paymentsStatsBar.value)
  }
  if (devicesStatsBar.value.length > 0) {
    paymentBars.push(devicesStatsBar.value)
  }
  return paymentBars
})

onMounted(async () => {
  await nextTick()
  fetchPaymentMethodsDataForRelatedContent()
})

const allPMsData = ref(null)

const fetchPaymentMethodsDataForRelatedContent = async () => {
  const data = await $fetch('/api/v3/payment-methods_data/payment-methods' + '/' + localeRef.value)
  allPMsData.value = data[localeRef.value]?.paymentsMethodsData
}

const mappedRelatedPaymentMethods = computed(() => {
  const items = pageContent.value?.content.relatedPaymentMethods
  if (!items || !allPMsData.value) {
    return null
  }

  const { getUrl } = useRoutes(store.routes, localeRef)
  const related = mapperRelatedPaymentMethodsDetail(items, allPMsData.value, getUrl)
  return related
})
</script>

<style lang="scss" scoped>
/* stylelint-disable */
:deep() .ds-image-element {
  aspect-ratio: 2/1;
}
</style>
